// eslint-disable-next-line import/no-extraneous-dependencies
import { useDropzone, type DropzoneOptions, type DropzoneRootProps, type DropzoneInputProps } from 'react-dropzone';

type AcceptedFileTypes = Record<string, string[]>;

export interface UseFileDropzoneProps {
  acceptedFileTypes: AcceptedFileTypes;
  fileTypeDisplay?: string;
  maxFiles?: number;
  disabled?: boolean;
  onAcceptedFiles?: (files: File | File[]) => void;
  onRejectedFiles?: (errorMessage: string) => void;
}

export interface UseFileDropzoneReturn {
  getRootProps: () => DropzoneRootProps;
  getInputProps: () => DropzoneInputProps;
  isDragActive: boolean;
}

export function useFileDropzone({
  acceptedFileTypes,
  fileTypeDisplay = 'supported files',
  maxFiles = 1,
  disabled = false,
  onAcceptedFiles,
  onRejectedFiles,
}: UseFileDropzoneProps): UseFileDropzoneReturn {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: acceptedFileTypes,
    maxFiles: maxFiles || 0, // 0 means unlimited in react-dropzone
    multiple: maxFiles > 1,
    disabled,
    onDropAccepted: (files: File[]) => {
      onAcceptedFiles?.(maxFiles === 1 ? files[0] : files);
    },
    onDropRejected: (rejectedFiles) => {
      const rejection = rejectedFiles[0];
      const errorCode = rejection.errors[0]?.code;

      let errorMessage = 'Invalid file format';
      if (errorCode === 'file-invalid-type') {
        errorMessage = `Please upload ${fileTypeDisplay}`;
      } else if (errorCode === 'too-many-files') {
        errorMessage = maxFiles
          ? `Please upload only ${maxFiles} file${maxFiles > 1 ? 's' : ''} at a time`
          : 'Too many files selected';
      }

      onRejectedFiles?.(errorMessage);
    },
  } as DropzoneOptions);

  return {
    getRootProps,
    getInputProps,
    isDragActive,
  };
}
